import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Flight,
  Refresh,
  Speed,
  Height,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { liveFlightService, ProcessedFlight } from '../../services/liveFlightService';

// Fix Leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const StatsCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
    : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

const MapContainer_Styled = styled(Box)(({ theme }) => ({
  height: 400, // Smaller height for embedded version
  borderRadius: 12,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '& .leaflet-container': {
    borderRadius: 12,
  },
  // Ensure polylines are properly rendered
  '& .leaflet-overlay-pane svg': {
    pointerEvents: 'auto',
  },
  '& .leaflet-interactive': {
    cursor: 'pointer',
  },
}));

// Utility function to get airline colors
const getAirlineColor = (airline: string): string => {
  const colors = [
    '#FF4444', '#00BFFF', '#32CD32', '#FF8C00', '#9370DB',
    '#FF69B4', '#00CED1', '#FFD700', '#FF6347', '#40E0D0',
    '#FFA500', '#98FB98', '#F0E68C', '#DDA0DD', '#87CEEB'
  ];
  
  let hash = 0;
  for (let i = 0; i < airline.length; i++) {
    hash = airline.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Flight markers component
const FlightMarkers: React.FC<{ 
  flights: ProcessedFlight[], 
  showOnGround: boolean,
  onFlightClick: (flight: ProcessedFlight) => void,
  filterAirlines?: string[] // Optional filter for specific airlines
}> = ({ flights, showOnGround, onFlightClick, filterAirlines }) => {
  
  const filteredFlights = flights.filter(flight => {
    // Skip ground aircraft if not showing them
    if (!showOnGround && flight.isOnGround) return false;
    
    // Filter by specific airlines if provided
    if (filterAirlines && filterAirlines.length > 0) {
      return filterAirlines.some(airline => 
        flight.airline.toLowerCase().includes(airline.toLowerCase())
      );
    }
    
    return true;
  });

  return (
    <>
      {filteredFlights.map((flight) => {
        return (
          <React.Fragment key={flight.id}>
            {/* Flight Path - Enhanced for better visibility */}
            {flight.path && flight.path.length > 1 && (
              <>
                {/* Background stroke for better visibility */}
                <Polyline
                  positions={flight.path}
                  pathOptions={{
                    color: '#ffffff',
                    weight: 4,
                    opacity: 0.6,
                  }}
                />
                {/* Main flight path */}
                <Polyline
                  positions={flight.path}
                  pathOptions={{
                    color: getAirlineColor(flight.airline),
                    weight: 3,
                    opacity: 0.9,
                    dashArray: flight.isOnGround ? '8, 4' : undefined,
                  }}
                />
              </>
            )}
            
            {/* Aircraft Marker */}
            <Marker
              position={[flight.latitude, flight.longitude]}
              eventHandlers={{
                click: () => onFlightClick(flight),
              }}
              icon={L.divIcon({
                html: `
                  <div style="
                    transform: rotate(${flight.heading || 0}deg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 24px;
                    height: 24px;
                    cursor: pointer;
                  ">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="${getAirlineColor(flight.airline)}" style="
                      filter: drop-shadow(1px 1px 3px rgba(0,0,0,0.6));
                      stroke: #ffffff;
                      stroke-width: 0.5;
                    ">
                      <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z"/>
                    </svg>
                  </div>
                `,
                className: 'flight-marker',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

interface EmbeddedFlightMapProps {
  height?: number;
  userAirlines?: string[]; // Airlines user has programs with
  showControls?: boolean;
  title?: string;
}

export const EmbeddedFlightMap: React.FC<EmbeddedFlightMapProps> = ({ 
  height = 400, 
  userAirlines = [],
  showControls = true,
  title = "Live Flight Tracking"
}) => {
  const [flights, setFlights] = useState<ProcessedFlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnGround, setShowOnGround] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState<ProcessedFlight | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterUserAirlines, setFilterUserAirlines] = useState(false);

  // Load initial flight data
  useEffect(() => {
    loadFlights();
  }, []);

  const loadFlights = async () => {
    setLoading(true);
    try {
      const flightData = await liveFlightService.getAllFlights();
      setFlights(flightData);
    } catch (error) {
      console.error('Error loading flights:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshFlights = async () => {
    setRefreshing(true);
    try {
      const flightData = await liveFlightService.getAllFlights();
      setFlights(flightData);
    } catch (error) {
      console.error('Error refreshing flights:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filteredFlights = flights.filter(flight => {
    // Filter by user's airlines if enabled
    if (filterUserAirlines && userAirlines.length > 0) {
      const hasUserAirline = userAirlines.some(userAirline => 
        flight.airline.toLowerCase().includes(userAirline.toLowerCase()) ||
        userAirline.toLowerCase().includes(flight.airline.toLowerCase())
      );
      if (!hasUserAirline) {
        return false;
      }
    }
    
    return showOnGround || !flight.isOnGround;
  });

  // Debug logging when user airline filtering is enabled
  useEffect(() => {
    if (filterUserAirlines && userAirlines.length > 0) {
      console.log('🛩️ EmbeddedFlightMap - User airlines for filtering:', userAirlines);
      console.log('🛩️ EmbeddedFlightMap - Total flights available:', flights.length);
      console.log('🛩️ EmbeddedFlightMap - Filtered flights count:', filteredFlights.length);
      console.log('🛩️ EmbeddedFlightMap - Sample filtered flights:', filteredFlights.slice(0, 3).map(f => ({ airline: f.airline, callsign: f.callsign })));
    }
  }, [filterUserAirlines, userAirlines, filteredFlights.length, flights.length]);

  const stats = liveFlightService.getFlightStats(filteredFlights);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        
        {showControls && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {userAirlines.length > 0 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={filterUserAirlines}
                    onChange={(e) => setFilterUserAirlines(e.target.checked)}
                    size="small"
                  />
                }
                label="My Airlines Only"
                sx={{ mr: 2 }}
              />
            )}
            
            <FormControlLabel
              control={
                <Switch
                  checked={showOnGround}
                  onChange={(e) => setShowOnGround(e.target.checked)}
                  size="small"
                />
              }
              label="Ground Aircraft"
              sx={{ mr: 2 }}
            />
            
            <Button
              onClick={refreshFlights}
              disabled={refreshing}
              size="small"
              startIcon={refreshing ? <div /> : <Refresh />}
            >
              {refreshing ? 'Updating...' : 'Refresh'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Stats */}
      {stats && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 1.5, 
          mb: 2
        }}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {stats.total}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Live Flights
              </Typography>
            </CardContent>
          </StatsCard>
          
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {Math.round(stats.averageAltitude)}ft
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Avg Altitude
              </Typography>
            </CardContent>
          </StatsCard>
          
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {stats.topAirlines.length}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Airlines
              </Typography>
            </CardContent>
          </StatsCard>
        </Box>
      )}

      {/* Map */}
      <MapContainer_Styled sx={{ height }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            gap: 2
          }}>
            <div>Loading flight data...</div>
          </Box>
        ) : (
          <MapContainer
            center={[20, 77]}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlightMarkers 
              flights={filteredFlights} 
              showOnGround={showOnGround}
              onFlightClick={(flight) => {
                setSelectedFlight(flight);
                setDialogOpen(true);
              }}
              filterAirlines={filterUserAirlines ? userAirlines : undefined}
            />
          </MapContainer>
        )}
      </MapContainer_Styled>

      {/* Flight Details Dialog - Compact Version */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: selectedFlight ? getAirlineColor(selectedFlight.airline) : 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 2
        }}>
          <Flight />
          {selectedFlight?.callsign || 'Unknown Flight'}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {selectedFlight && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Route */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                borderRadius: 2
              }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="textSecondary">FROM</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedFlight.origin || 'Unknown'}
                  </Typography>
                </Box>
                
                <Flight sx={{ color: getAirlineColor(selectedFlight.airline) }} />
                
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="textSecondary">TO</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedFlight.destination || 'Unknown'}
                  </Typography>
                </Box>
              </Box>

              {/* Key Details */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Airline</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedFlight.airline}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Type</Typography>
                  <Chip
                    label={selectedFlight.flightType || 'Unknown'}
                    color={selectedFlight.flightType === 'Domestic' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmbeddedFlightMap;